import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Product } from "../../types/Product";
import { formatMoney } from "../../styles/liquidGlass";
import { Icon, ImgPh } from "../liquid/LiquidPrimitives";
import QuickAddDialog from "./QuickAddDialog";

interface ProductCardProps {
  product: Product;
  variant?: "carousel" | "showcase-small" | "showcase-large" | "carousel-border";
  showDiscount?: boolean;
  discountText?: string;
  onClick?: (productSku: string) => void;
  layout?: "card" | "row";
}

export default function ProductCard({
  product,
  variant,
  showDiscount = false,
  discountText = "Nuevo",
  onClick,
  layout = "card",
}: ProductCardProps) {
  const [quickAddOpen, setQuickAddOpen] = useState(false);

  const href = `/productos/${product.sku}`;
  const isOutOfStock = product.stock != null && product.stock <= (product.stockMinimoPublicacion ?? 0);
  const isLarge = variant === "showcase-large";

  const handleLinkClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (!onClick) return;
    const isLeftClick = event.button === 0;
    const isModified = event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
    if (isLeftClick && !isModified) {
      event.preventDefault();
      onClick(product.sku);
    }
  };

  const handleBagClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setQuickAddOpen(true);
  };

  if (layout === "row") {
    return (
      <>
        <Link
          to={href}
          onClick={handleLinkClick}
          className="lg"
          style={{
            display: "grid",
            gridTemplateColumns: "124px 1fr auto",
            gap: 18,
            alignItems: "center",
            padding: 12,
            borderRadius: 24,
            textDecoration: "none",
            color: "inherit",
            width: "100%",
            minHeight: 148,
          }}
        >
          <div style={{ borderRadius: 18, overflow: "hidden", height: 124, background: "linear-gradient(160deg, var(--cream-100), var(--cream-300))" }}>
            <ImgPh label={product.nombre} src={product.urlImagenPrincipal} ratio="auto" style={{ height: "100%", borderRadius: 18 }} />
          </div>

          <div style={{ minWidth: 0 }}>
            <div className="eyebrow" style={{ marginBottom: 8 }}>{isOutOfStock ? "Sin stock" : "Disponible"}</div>
            <div
              style={{
                fontSize: 18,
                fontWeight: 600,
                letterSpacing: "-0.02em",
                color: "var(--ink-900)",
                textTransform: "uppercase",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {product.nombre}
            </div>
            <div style={{ marginTop: 8, color: "var(--ink-500)", fontSize: 13 }}>
              SKU {product.sku}
            </div>
            <div className="mono" style={{ marginTop: 12, fontSize: 20, fontWeight: 600 }}>
              {formatMoney(product.precio)}
            </div>
          </div>

          <button
            type="button"
            onClick={handleBagClick}
            aria-label="Elegir variante y cantidad"
            style={{
              width: 46,
              height: 46,
              borderRadius: 999,
              background: "var(--ink-900)",
              color: "var(--cream-50)",
              border: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              boxShadow: "0 6px 18px -6px rgba(0,0,0,0.4)",
              flexShrink: 0,
            }}
          >
            <Icon.bag color="var(--cream-50)" />
          </button>
        </Link>

        <QuickAddDialog open={quickAddOpen} product={product} onClose={() => setQuickAddOpen(false)} />
      </>
    );
  }

  return (
    <>
      <Link
        to={href}
        onClick={handleLinkClick}
        style={{
          position: "relative",
          borderRadius: 24,
          overflow: "hidden",
          cursor: "pointer",
          background: "rgba(255,250,240,0.4)",
          transition: "transform .25s ease",
          display: "block",
          width: "100%",
        }}
        onMouseEnter={(event) => {
          event.currentTarget.style.transform = "translateY(-3px)";
        }}
        onMouseLeave={(event) => {
          event.currentTarget.style.transform = "translateY(0)";
        }}
      >
        <div
          style={{
            position: "relative",
            aspectRatio: isLarge ? "1/1.1" : "1/1.05",
            background: "linear-gradient(160deg, var(--cream-100), var(--cream-300))",
            borderRadius: 24,
            overflow: "hidden",
          }}
        >
          <ImgPh
            label={product.nombre}
            ratio="auto"
            src={product.urlImagenPrincipal}
            style={{
              height: "100%",
              borderRadius: 0,
              background: "linear-gradient(160deg, var(--cream-100), var(--cream-300))",
            }}
          />

          {(showDiscount || isOutOfStock) && (
            <div
              className={isOutOfStock ? "lg-dark" : "lg"}
              style={{
                position: "absolute",
                top: 14,
                left: 14,
                padding: "6px 12px",
                borderRadius: 999,
                fontSize: 10.5,
                fontWeight: 600,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
              }}
            >
              {isOutOfStock ? "Sin stock" : discountText}
            </div>
          )}

          <div
            className="lg"
            style={{
              position: "absolute",
              left: 12,
              right: 12,
              bottom: 12,
              padding: "12px 16px",
              borderRadius: 18,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 10,
            }}
          >
            <div style={{ minWidth: 0, flex: 1 }}>
              <div
                style={{
                  fontSize: 12.5,
                  fontWeight: 500,
                  color: "var(--ink-700)",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                }}
              >
                {product.nombre}
              </div>
              <div className="mono" style={{ fontSize: 14.5, fontWeight: 600, marginTop: 2 }}>
                {formatMoney(product.precio)}
              </div>
            </div>

            <button
              type="button"
              onClick={handleBagClick}
              aria-label="Elegir variante y cantidad"
              style={{
                width: 38,
                height: 38,
                borderRadius: 999,
                background: "var(--ink-900)",
                color: "var(--cream-50)",
                border: "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                boxShadow: "0 6px 18px -6px rgba(0,0,0,0.4)",
                flexShrink: 0,
              }}
            >
              <Icon.bag color="var(--cream-50)" />
            </button>
          </div>
        </div>
      </Link>

      <QuickAddDialog open={quickAddOpen} product={product} onClose={() => setQuickAddOpen(false)} />
    </>
  );
}
